import { getKvSyncApi } from '@/api/kvSync'
import Socket from '@/socket'
import slide from '@/api/slide'
import sentry from '@/services/sentry'

const actions = {
  // Repairs a nextback that never reached aha-sync: republishes the presentation's active slide
  // over /activeSlide/<presentationId> at editor boot, on the assumption that `presentation`
  // .activeSlide is the truth and the KV doc is the copy that may have gone stale.
  //
  // AHA-43274: that assumption is inverted in the PowerPoint add-in, so this path is skipped there.
  // A deck boots ONE FRAME PER SLIDE, each a full presenter instance, and this publishes
  // `presentation.activeSlide` — a presentation-wide server field naming whichever frame claimed
  // last — rather than `slideActive.id`, the frame's own slide. Every frame therefore republishes a
  // slide that is by definition not its own, under its own socketId, and (worse) pairs that id with
  // `slideActive.quizStatus` read off a DIFFERENT slide.
  //
  // It lands after the frame's own startActiveSlidePPT claim: both are fired un-awaited on adjacent
  // lines of the boot path (PresentationEditor.handleDataPresentationDetail), so the claim's PUT is
  // still in flight when the KV GET below resolves and reads the not-yet-updated activeSlide. The
  // KV path has no CAS, so last write wins and the audience is left on the stale slide.
  //
  // The guard below cannot catch it: it returns early only when the KV doc already agrees with
  // `presentation.activeSlide`, i.e. exactly when the write would have been harmless. When the KV
  // doc holds the correct fresh id and `presentation.activeSlide` is the stale one, the comparison
  // fails and the fallback overwrites correct with stale.
  //
  // Nothing is lost by skipping: the add-in has its own claim path (startActiveSlidePPT) which
  // publishes the frame's real slide on every show change, so there is no missed nextback to repair.
  fallbackNextBackToAhaSync: async ({ rootState }) => {
    if (rootState.isEnableAddinPowerpoint) return
    try {
      const resp = await getKvSyncApi().getValueByKey(`/activeSlide/${rootState.presentation.id}`)
      if (resp.body && resp.body.value) {
        const value = JSON.parse(resp.body.value)
        if (value.id === rootState.presentation.activeSlide) {
          return
        }
      }
      if (!rootState.presentation.activeSlide) {
        return
      }
      const payload = {
        activeSlide: rootState.presentation.activeSlide,
        socketId: Socket.getSocketId(),
        accessCode: rootState.presentation.uniqueAccessCode,
        isNextSlide: true,
        timestamp: new Date().getTime(),
        id: rootState.presentation.activeSlide,
        quizStatus: rootState.slideActive.quizStatus,
      }
      slide.syncActiveSlideState(rootState.presentation.id, payload)
    } catch (error) {
      sentry.captureException(error)
    }
  },
}

export default {
  namespaced: true,
  actions,
}
